import { useState } from "react";
import { Box, Menu, MenuItem, Popover, Tooltip } from "@mui/material";
import { MousePointerClick, Plus } from "lucide-react";
import { toast } from "react-toastify";

import { Button } from "../../../components/Button";
import { CustomTextField } from "../../../components/CustomTextField";
import { MarkdownEditor } from "../../../components/MarkdownEditor";
import { brand } from "../../../theme/theme";
import { ContentPreviewCard } from "./ContentPreviewCard";
import { ADAPTATION_PLATFORMS, PlatformIcon, getPlatformMeta } from "./platformMeta";
import { readVideoMetadata } from "./VideoMedia";
import { textEditorHeight } from "./workflowUtils";
import {
  CALL_TO_ACTIONS,
  NO_CALL_TO_ACTION,
  callToActionHost,
  callToActionMeta,
  normalizeCallToActionUrl,
} from "./callToAction";

export const hasText = (text) => Boolean(String(text || "").trim());

const localId = () => `local-${Date.now()}-${Math.random()}`;

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

/** Local image ({ id, image: previewUrl, file, width, height }); null size when the browser can't read it. */
const toLocalImage = async (file) => {
  const url = URL.createObjectURL(file);
  const size = await loadImage(url).then(
    (image) => ({ width: image.naturalWidth, height: image.naturalHeight }),
    () => ({ width: null, height: null })
  );
  return { id: localId(), image: url, file, ...size };
};

const KEPT_CROP_TYPES = ["image/png", "image/webp"];

/** `box` of a local image as a new local image, cut in the browser; other types than PNG/WebP become JPEG. */
const cropLocalImage = async (image, box) => {
  const source = await loadImage(image.image);
  const canvas = document.createElement("canvas");
  canvas.width = box.width;
  canvas.height = box.height;
  canvas.getContext("2d").drawImage(source, box.x, box.y, box.width, box.height, 0, 0, box.width, box.height);
  const type = KEPT_CROP_TYPES.includes(image.file.type) ? image.file.type : "image/jpeg";
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, type, 0.92));
  if (!blob) throw new Error("Canvas crop failed");
  const name = type === image.file.type ? image.file.name : image.file.name.replace(/\.[^.]*$/, "") + ".jpg";
  const file = new File([blob], name, { type });
  return { id: localId(), image: URL.createObjectURL(file), file, width: box.width, height: box.height };
};

const revokeIfLocal = (url) => {
  if (typeof url === "string" && url.startsWith("blob:")) URL.revokeObjectURL(url);
};

export const revokeLocalImages = (images) =>
  (images || []).forEach((image) => {
    revokeIfLocal(image.image);
    revokeIfLocal(image.video_url);
    revokeIfLocal(image.document);
  });

/** Local video ({ media_type: "video", video_url: previewUrl, file, duration… }), uploaded after the piece exists. */
export const toLocalVideo = async (file) => {
  const url = URL.createObjectURL(file);
  const meta = await readVideoMetadata(url);
  return {
    id: localId(),
    media_type: "video",
    image: null,
    video_url: url,
    file,
    file_size: file.size,
    ...meta,
  };
};

/** Local image swapped in place (crop); frees the old preview. */
export const replaceLocalImage = (images, old, next) => {
  revokeLocalImages([old]);
  return images.map((item) => (item.id === old.id ? next : item));
};

/** "+" chip that opens the list of networks not added yet. */
export function AddNetworkButton({ usedPlatforms, onAdd, buttonRef = null }) {
  const [anchor, setAnchor] = useState(null);
  const available = ADAPTATION_PLATFORMS.filter((platform) => !usedPlatforms.includes(platform));
  if (!available.length) return null;

  return (
    <>
      <Box
        ref={buttonRef}
        component="button"
        type="button"
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-label="Añadir red social"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 1.25,
          py: 0.5,
          borderRadius: "999px",
          border: "1px dashed #CBD5E1",
          backgroundColor: "#fff",
          color: "#374151",
          fontSize: "0.8125rem",
          fontWeight: 600,
          cursor: "pointer",
          "&:hover": { borderColor: brand.pink, color: brand.pinkDark },
        }}
      >
        <Plus size={14} /> Añadir red
      </Box>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {available.map((platform) => (
          <MenuItem
            key={platform}
            onClick={() => {
              setAnchor(null);
              onAdd(platform);
            }}
            sx={{ gap: 1.25, fontSize: "0.875rem" }}
          >
            <PlatformIcon platform={platform} size={22} />
            {getPlatformMeta(platform).label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * Card of a network written by hand, not saved yet: the same card as the review
 * step with its text always in the editor and its images kept locally.
 */
export function ManualNetworkCard({
  platform,
  text,
  images,
  onTextChange,
  onAddImages, // (localImages) => void
  onRemoveImage, // (image) => void
  onCropImage = null, // (image, croppedLocalImage) => void
  format = "post",
  onChangeFormat = null, // (format) => void — local until the piece is saved
  onAddVideo = null, // (file, { onProgress, signal }) => Promise — local until saved, or uploaded right away
  callToAction = NO_CALL_TO_ACTION, // Google Business button: { type, url }
  onChangeCallToAction = null, // ({ type, url }) => void — local until the piece is saved
  onAddDocument = null, // (file, title) => void — local until the piece is saved
  onChangeDocumentTitle = null, // (title) => void
  onUploadInlineImage, // (file) => Promise<url|null> — images inside the text
  selected = false,
  onSelect,
  actions = null,
  embedded = false,
}) {
  const draft = {
    id: `new-${platform}`,
    platform,
    text,
    images,
    format,
    call_to_action: callToAction.type,
    call_to_action_url: callToAction.url,
  };

  return (
    <ContentPreviewCard
      adaptations={[draft]}
      hideTitleInHeader
      status={{ label: "Sin guardar", color: "muted" }}
      selected={selected}
      onSelect={onSelect}
      onOpenImage={(url) => window.open(url, "_blank", "noopener")}
      onAddImages={async (_item, files) => onAddImages(await Promise.all(files.map(toLocalImage)))}
      onRemoveImage={async (_item, image) => onRemoveImage(image)}
      onCropImage={
        onCropImage
          ? async (_item, image, box) => {
            try {
              onCropImage(image, await cropLocalImage(image, box));
            } catch (error) {
              toast.error("No se ha podido recortar la imagen.");
              throw error; // the crop dialog stays open
            }
          }
          : null
      }
      onAddVideo={onAddVideo ? async (_item, file, options) => onAddVideo(file, options) : null}
      onChangeFormat={onChangeFormat ? async (_item, value) => onChangeFormat(value) : null}
      onAddDocument={onAddDocument ? async (_item, file, title) => onAddDocument(file, title) : null}
      onChangeDocumentTitle={onChangeDocumentTitle ? async (_item, title) => onChangeDocumentTitle(title) : null}
      textEditor={
        // Keyed per network: the editor keeps its own HTML state between value changes.
        <MarkdownEditor
          key={platform}
          value={text || ""}
          onChange={onTextChange}
          minHeight="198px"
          initialHeight={textEditorHeight(platform)} // the corner handle resizes it
          resizable
          hideBorder={embedded} // the card's frame wraps the editor and its media
          placeholder={`Escribe o pega aquí el texto para ${getPlatformMeta(platform).label}...`}
          allowHeadings={platform === "blog"}
          allowImages={getPlatformMeta(platform).inlineImages}
          onImageUpload={onUploadInlineImage}
          toolbarActions={
            platform === "google_business_profile" && onChangeCallToAction ? (
              <CallToActionPicker value={callToAction} busy={false} onChange={onChangeCallToAction} />
            ) : null
          }
        />
      }
      actions={actions}
      embedded={embedded}
    />
  );
}

/**
 * Google Business post button in the editor toolbar: flat when off, soft pink (the app's active chip) when on.
 * Opens a popover to pick the button and, except "Llamar ahora", the link it opens.
 * The trigger is a span: Quill styles every toolbar <button>.
 */
export function CallToActionPicker({ value = NO_CALL_TO_ACTION, busy, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [type, setType] = useState("");
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [saving, setSaving] = useState(false);
  const current = callToActionMeta(value.type);
  const picked = callToActionMeta(type);
  const CurrentIcon = current?.icon || MousePointerClick;
  const disabled = busy || saving;

  const open = (event) => {
    if (disabled) return;
    setType(value.type || CALL_TO_ACTIONS[0].value);
    setUrl(value.url || "");
    setUrlError("");
    setAnchorEl(event.currentTarget);
  };
  const close = () => !saving && setAnchorEl(null);

  const apply = async (next) => {
    setSaving(true);
    try {
      await onChange(next);
      setAnchorEl(null);
    } finally {
      setSaving(false);
    }
  };

  const save = () => {
    if (!picked?.needsUrl) return apply({ type, url: "" });
    const normalized = normalizeCallToActionUrl(url);
    if (!normalized) {
      setUrlError(url.trim() ? "Escribe una dirección web válida, p. ej. https://tuweb.com" : "Indica el enlace al que lleva el botón.");
      return;
    }
    return apply({ type, url: normalized });
  };

  const linkShown = current?.needsUrl && value.url;

  return (
    <>
      <Tooltip
        title={linkShown ? "" : current ? "Llama al teléfono de la ficha" : "Botón de la publicación en Google"}
        arrow
      >
        <Box
          component="span"
          role="button"
          tabIndex={0}
          aria-haspopup="dialog"
          aria-pressed={Boolean(current)}
          aria-busy={disabled || undefined}
          onClick={open}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              open(event);
            }
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            minWidth: 0,
            maxWidth: "100%",
            overflow: "hidden",
            borderRadius: "8px",
            fontSize: "0.8125rem",
            fontWeight: 600,
            whiteSpace: "nowrap",
            userSelect: "none",
            color: current ? brand.pink : "#6B7280",
            backgroundColor: current ? brand.soft : "transparent",
            border: `1px solid ${current ? brand.softBorder : "transparent"}`,
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? "default" : "pointer",
            transition: "background-color 0.15s, color 0.15s",
            "&:hover": current ? {} : { color: "#111827" },
            "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 1 },
          }}
        >
          <CurrentIcon size={15} style={{ flexShrink: 0 }} />
          <Box component="span" sx={{ flexShrink: 0 }}>
            {current ? `Botón «${current.label}»` : "Añadir botón"}
          </Box>
          {linkShown ? (
            <Box component="span" sx={{ fontWeight: 500, opacity: 0.75, minWidth: 0, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>
              · {callToActionHost(value.url)}
            </Box>
          ) : null}
        </Box>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { mt: 0.5, p: 2, width: 340, maxWidth: "calc(100vw - 32px)", borderRadius: "12px" } } }}
      >
        <Box sx={{ fontSize: "0.875rem", fontWeight: 700, color: "#111827", mb: 0.25 }}>Botón de la publicación</Box>
        <Box sx={{ fontSize: "0.75rem", color: "#6B7280", mb: 1.5 }}>Aparece debajo del texto en la ficha de Google.</Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 0.75 }}>
          {CALL_TO_ACTIONS.map((option) => {
            const Icon = option.icon;
            const active = option.value === type;
            return (
              <Box
                key={option.value}
                component="button"
                type="button"
                onClick={() => {
                  setType(option.value);
                  setUrlError("");
                }}
                aria-pressed={active}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: "8px",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  fontFamily: "inherit",
                  textAlign: "left",
                  cursor: "pointer",
                  color: active ? brand.pink : "#374151",
                  backgroundColor: active ? brand.soft : "#fff",
                  border: `1px solid ${active ? brand.softBorder : "#E5E7EB"}`,
                  "&:hover": active ? {} : { borderColor: "#D1D5DB", backgroundColor: "#F9FAFB" },
                  "&:focus-visible": { outline: `2px solid ${brand.pink}`, outlineOffset: 1 },
                }}
              >
                <Icon size={15} />
                {option.label}
              </Box>
            );
          })}
        </Box>
        {picked?.needsUrl ? (
          <Box sx={{ mt: 1.5 }}>
            <Box component="label" htmlFor="call-to-action-url" sx={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "#374151", mb: 0.5 }}>
              Enlace del botón
            </Box>
            <CustomTextField
              id="call-to-action-url"
              value={url}
              onChange={(event) => {
                setUrl(event.target.value);
                setUrlError("");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  save();
                }
              }}
              placeholder="https://tuweb.com/reservas"
              error={Boolean(urlError)}
              errorMessage={urlError}
              autoFocus
            />
          </Box>
        ) : (
          picked && (
            <Box sx={{ mt: 1.5, fontSize: "0.75rem", color: "#6B7280" }}>
              Llama al teléfono de la ficha de Google. Google no admite teléfonos en el texto.
            </Box>
          )
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          {current && (
            <Button variant="ghost" size="sm" disabled={saving} onClick={() => apply(NO_CALL_TO_ACTION)}>
              Quitar botón
            </Button>
          )}
          <Button size="sm" disabled={saving || !picked} onClick={save}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
